/**
 * GlobalConfigContext and Provider
 *
 * @fileoverview
 * Context and Provider to manage GlobalConfig from Contentful.
 * Config is automatically fetched based on current siteId and provided
 * through context so that components can easily access it.
 */

import React, {createContext, useContext, useMemo} from 'react';
import PropTypes from 'prop-types';

import {useGlobalConfigBySite} from '@amblique/king-living/app/contentful/hooks/use-global-config';

/**
 * GlobalConfigContext
 */
const GlobalConfigContext = createContext(null);

/**
 * GlobalConfigProvider
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {Contentful.GlobalConfig|null} [props.fallbackConfig=null] - Fallback config
 * @returns {React.ReactElement}
 */
export function GlobalConfigProvider({children, fallbackConfig = null}) {
  const {data: globalConfig, isLoading, isFetched, error} = useGlobalConfigBySite({
    queryOptions: {
      staleTime: 15 * 60 * 1000,
    },
  });

  const config = globalConfig || fallbackConfig;

  const isReady = isFetched || !!fallbackConfig;

  const value = useMemo(() => {
    return {
      config,
      isLoading,
      isFetched,
      isReady,
      error,

      siteName: config?.siteName || null,
      siteId: config?.siteId || null,
      availableRegions: config?.availableRegions || [],
      socialLinks: config?.socialLinksCollection?.items || [],
      pdpPreferences: config?.pdpPreferences || null,
      signUpHeader: config?.signUpHeader || null,
      signUpImage: config?.signUpImage || null,
      loginImage: config?.loginImage || null,
      cartPaymentIcons: config?.cartPaymentIconsCollection?.items || [],
      accountHero: config?.accountHero ? [config.accountHero] : [],
      accountSuggestedForYou: config?.accountSuggestedForYou ?? null,
      accountGetInspired: config?.accountGetInspired ?? null,
      accountOrderHistoryFaq: config?.accountOrderHistoryFaq ?? null,
      accountShopNowLink: config?.accountShopNowLink ?? null,
    };
  }, [config, isLoading, isFetched, isReady, error]);

  return (
    <GlobalConfigContext.Provider value={value}>
      {children}
    </GlobalConfigContext.Provider>
  );
}

GlobalConfigProvider.propTypes = {
  children: PropTypes.node.isRequired,
  fallbackConfig: PropTypes.object,
};

/**
 *
 * @returns {Object} Global config object
 * @throws {Error} If used outside GlobalConfigProvider
 *
 * @example
 * // Basic usage
 * const {siteName, socialLinks, pdpPreferences} = useGlobalConfig();
 *
 * @example
 * // With loading state
 * const {siteName, isLoading} = useGlobalConfig();
 * if (isLoading) return <Loading />;
 *
 * @example
 * // With error handling
 * const {config, error} = useGlobalConfig();
 * if (error) return <Error />;
 */
export function useGlobalConfig() {
  const context = useContext(GlobalConfigContext);

  if (context === null) {
    throw new Error('useGlobalConfig must be used within GlobalConfigProvider');
  }

  return context;
}
